<template>
  <div>
    <div class="c-searchPage">
      <div class="c-searchPage_breadcrumb">
        <SearchBreadcrumb
          :bread="bread"
          :conditions="search.condition"
          :isLoading="isLoading"
        />
      </div>
      <div
        class="c-columns"
        :class="{ 'c-columns-loading': isLoading }"
      >
        <div class="c-columns_side">
          <ClientOnly>
            <RequestSearchForm
              @change="changeSearchForm($event)"
              :queries="$util.castQueryFields($route.query)"
              :keyword="keyword"
              :categoryId="currentCategoryId"
              :categoryTitle="currentCategoryTitle"
              :conditions="search.condition"
              :isLoading="isLoading"
            />
          </ClientOnly>
        </div>
        <div class="c-columns_main">
          <div class="c-btnAddRequest">
            <CButton
              :href="$coconala.url('/requests/add')"
              color="secondary"
              is-bold
            >
              <slot>
                <span v-if="!isSP">公開依頼を投稿する</span>
                <span v-else>依頼を投稿</span>
              </slot>
            </CButton>
          </div>
          <div class="c-searchTitle">
            <h1>
              <span class="c-searchTitleCaption">{{ currentCategoryTitle }}</span>
              の仕事・相談を探す
            </h1>
          </div>
          <div class="c-searchKeyword">
            <SearchInput
              initialPlaceholder="どのような仕事・相談をお探しですか？"
              initialStyle="c-requestSearchKeyword"
              :dropdown="false"
              @change="changeKeyword($event.searchText)"
              expanded
            />
            <div
              class="c-searchKeywordDescription"
              v-if="!isSP"
            >
              <div class="flexItem flexItemExample">検索例）ホームページ制作 デザイン</div>
              <div class="flexItem flexItemHelp">
                初めて利用する方は
                <a
                  :href="$coconala.url('/pages/guide_request')"
                  target="_blank"
                >こちら</a>
              </div>
            </div>
          </div>
          <div class="c-searchHeader">
            <div class="c-searchHeader_result">
              <div class="c-searchHeaderHits">
                <span class="c-searchHeader_resultHits">{{ totalCountText }}</span>
                件中
                <span v-if="isLoading">-</span>
                <span v-else-if="search.total === 0">0</span>
                <span v-else>{{ startCountText }} - {{ endCountText }}</span>
                件表示
              </div>
              <div class="c-searchHeaderRecruiting">
                <SearchRecruiting
                  v-model="recruiting"
                  @input="changeRecruiting($event)"
                />
              </div>
            </div>
            <div class="c-searchHeader_sort">
              <CSelect
                v-model="sortType"
                size="is-medium"
                expanded
                @input="changeSortType"
              >
                <option :value="null">新着順</option>
                <option
                  v-for="type in SORT_TYPES"
                  :value="type.value"
                  :key="type.name"
                >{{ type.name }}</option>
              </CSelect>
            </div>
          </div>
          <div class="c-searchResults">
            <div
              class="c-searchPage_mainContent"
              :class="{ 'c-searchPage_mainContent-loading': isLoading }"
            >
              <BLoading
                class="c-searchPage_loader"
                :active="isLoading"
                :is-full-page="false"
              />
              <transition name="fade-in">
                <div
                  class="c-searchPage_itemList"
                  v-show="!isLoading"
                >
                  <div v-if="this.search.total > 0">
                    <RequestSearchItem
                      v-for="result in search.requestsList"
                      :result="result"
                      :key="result.requestId"
                    />
                  </div>
                  <div
                    class="c-searchNoHits"
                    v-else
                  >
                    <CoconalaIcon name="info-circle" />
                    該当する仕事・相談が見つかりませんでした。<br>
                    条件を変えて再度お試しください。
                  </div>
                </div>
              </transition>
            </div>
          </div>
          <div
            class="c-searchPage_pagination"
            :class="{'c-searchPage_pagination-app': isFromCoconalaApp}"
            v-show="!isLoading"
            v-if="this.search.total > 0"
          >
            <CPagination
              order="is-centered"
              :total="search.total"
              :per-page="search.limit"
              :current="Number($route.query.page || 1)"
              theme="pagination-gray"
              has-link
            />
            <div
              v-if="search.total"
              class="c-searchPage_paginationSummary"
            >
              <span class="c-searchHeader_resultNum">{{ totalCountText }}</span>
              件中
              <span v-if="isLoading">-</span>
              <span v-else>{{ startCountText }} - {{ endCountText }}</span>
              件
            </div>
          </div>
        </div>
      </div>
    </div>
    <TheVueport breakpointType="vueportRequests" />
  </div>
</template>
<script>
import SearchBreadcrumb from '~/components/molecules/SearchBreadcrumb'
import RequestSearchForm from '~/components/organisms/RequestSearchForm'
import RequestSearchItem from '~/components/organisms/RequestSearchItem'
import SearchInput from '~/components/molecules/SearchInput'
import SearchRecruiting from '~/components/organisms/RequestSearchForm/components/SearchRecruiting'
import CPagination from '~/components/molecules/CPagination'
import TheVueport from '~/components/organisms/TheVueport'

import { mapState, mapGetters, mapActions } from 'vuex'
import qs from 'qs'

import { REQUESTS_RECRUITING } from '~/constants/cookie'

const SORT_TYPES = [
  { value: 'budget_desc', name: '予算が高い順' },
  { value: 'deadline_asc', name: '応募期限順' },
  { value: 'proposal_count_desc', name: '提案が多い順' },
  { value: 'proposal_count_asc', name: '提案が少ない順' }
]

const INIT_BREAD = {
  id: null,
  url: '/requests',
  isCurrent: false,
  name: '仕事・相談を探す'
}

const FOOTER_BOTTOM_SP = '66px'

export default {
  name: 'request',
  components: {
    SearchInput,
    SearchRecruiting,
    SearchBreadcrumb,
    RequestSearchForm,
    RequestSearchItem,
    CPagination,
    TheVueport
  },
  head() {
    return this.head
  },
  data() {
    return {
      // keywordは文字列のためキャスト不要
      keyword: this.$route.query.keyword,
      // sortTypeは文字列のためキャスト不要
      sortType: this.$route.query.sortType,
      recruiting: this.$util.castToBooleanQueryString(this.$route.query.recruiting) === true,
      SORT_TYPES,
      isLoading: false,
      bread: {
        type: 'request',
        breadcrumb: [INIT_BREAD]
      },
      footerBottomBeforeMountedSp: '32px'
    }
  },
  watch: {
    $route() {
      this.searchRequests()
    }
  },
  created() {
    this.updateData()
    this.genBread()
  },
  computed: {
    ...mapState('pages/requests', ['search']),
    ...mapState('master', ['requestPrices']),
    ...mapGetters('master', ['flattenMasterCategories']),
    ...mapGetters('ui', ['isPC', 'isTB', 'isSP']),
    ...mapGetters('auth', ['isFromCoconalaApp']),
    currentCategoryId() {
      if (this.$route.params.categoryId) {
        return this.$util.castToNumberQueryString(this.$route.params.categoryId)
      }
      return null
    },
    currentCategoryTitle() {
      if (this.$route.params.categoryId) {
        return this._setCategoryTitle(this.$route.params.categoryId)
      }
      return 'すべて'
    },
    head() {
      let head = {
        title: this.metaTitle,
        link: [{ rel: 'canonical', href: this.metaCanonical }]
      }

      if (Number(this.search.total) === 0) {
        Object.assign(head, {
          meta: [
            { hid: 'robots', name: 'robots', content: 'noindex,nofollow' },
            {
              hid: 'description',
              name: 'description',
              content: this.metaDescrption
            }
          ]
        })
      } else {
        Object.assign(head, {
          meta: [
            {
              hid: 'description',
              name: 'description',
              content: this.metaDescrption
            }
          ]
        })
      }

      return head
    },
    metaTitle() {
      let title = '仕事・相談の依頼を探す'
      const category_name = this.currentCategoryTitle
      const keyword = this.keyword
      if (category_name && category_name !== 'すべて' && keyword) {
        title = `${category_name}（${keyword}）の仕事・相談の依頼を探す`
      } else if (category_name && category_name !== 'すべて') {
        title = `${category_name}の仕事・相談の依頼を探す`
      } else if (keyword) {
        title = `${keyword}の仕事・相談の依頼を探す`
      }
      const page = this.$util.castToNumberQueryString(this.$route.query.page)
      if (typeof page === 'number' && page > 1) {
        title = `${title} - ${page}ページ目`
      }
      return `${title} | ココナラ`
    },
    metaDescrption() {
      let description =
        'ココナラは「知識・スキル・経験」が気軽に売り買いできるオンラインのスキルマーケットです。オンライン上で全て完結するので、地方在住の方や在宅の方でも仕事を受けることができます。まずはどんな仕事・相談が依頼されているかチェックしましょう。'
      const category_name = this.currentCategoryTitle
      const keyword = this.keyword
      let prefix = ''
      if (category_name && category_name !== 'すべて' && keyword) {
        prefix = `${category_name}（${keyword}）`
      } else if (category_name && category_name !== 'すべて') {
        prefix = category_name
      } else if (keyword) {
        prefix = keyword
      }
      if (prefix) {
        description = `${prefix}の仕事・相談の依頼一覧。${description}`
      } else {
        description =
          '仕事・相談の依頼一覧。ココナラは「知識・スキル・経験」が気軽に売り買いできるオンラインのスキルマーケットです。発注者からの仕事や相談の依頼を探すことができます。まずはどんな仕事が依頼されているか今すぐチェック！'
      }
      return description
    },
    metaCanonical() {
      let query = {}
      if (this.keyword) {
        Object.assign(query, { keyword: this.$route.query.keyword })
      }
      const page = this.$util.castToNumberQueryString(this.$route.query.page)
      if (typeof page === 'number' && page > 1) {
        Object.assign(query, { page })
      }
      let queryString = qs.stringify(query, {
        arrayFormat: 'brackets',
        skipNulls: true
      })
      return `${this.$route.path}${queryString ? '?' + queryString : ''}`
    },
    totalCountText() {
      return this.isLoading ? '-' : this.$util.numberWithDelimiter(Number(this.search.total))
    },
    startCountText() {
      return this.$util.numberWithDelimiter(Number(this.search.offset) + 1)
    },
    endCountText() {
      let endCount = Number(this.search.offset) + Number(this.search.limit)
      if (endCount > Number(this.search.total)) {
        endCount = Number(this.search.total)
      }
      return this.$util.numberWithDelimiter(endCount)
    }
  },
  methods: {
    ...mapActions('ui', ['updateFooterBottomSp']),
    changeSortType(value) {
      const attribute = {
        sortType: value,
        categoryId: Number(this.currentCategoryId)
      }
      this.changeSearchForm(attribute)
    },
    async searchRequests() {
      this.isLoading = true
      this.updateData()
      this.$scrollTo(document.body, -1)
      await this.getSearchRequests()
    },
    async getSearchRequests() {
      await this.$store
        .dispatch('pages/requests/search/searchRequests', {
          params: this.$route.params,
          query: this.$util.castQueryFields(this.$route.query)
        })
        .then(() => {
          this.genBread()
          this.isLoading = false
        })
        .catch(grpcCode => {
          this.$notify({
            group: 'flash',
            title: 'システムエラーが発生しました',
            text: 'ブラウザを閉じて再度やり直してください',
            type: 'error',
            max: 1
          })
        })
    },
    changeSearchForm(query) {
      if (typeof query.recruiting === 'boolean') {
        this.recruiting = query.recruiting
      }
      let newQuery = { ...this.$util.castQueryFields(this.$route.query), ...query, page: 1 }
      // キーワード検索が行われてここにきた場合、その他の条件はリセットする
      if (query.keyword) {
        newQuery = { keyword: query.keyword }
        this.sortType = null
        this.recruiting = false
      }
      if (newQuery.categoryId) {
        const { categoryId, priceMin, priceMax } = newQuery
        const category = this.flattenMasterCategories.find(c => c.id === Number(categoryId))
        const lowestPrice = Math.max(...this.requestPrices.filter(rp => rp <= category.lowestPrice))
        // priceMin、priceMaxがカテゴリの公開依頼最低価格を下回る場合はその条件をリセットする
        if (category) {
          if (priceMin && Number(priceMin) < lowestPrice) {
            newQuery.priceMin = null
          }
          if (priceMax && Number(priceMax) < lowestPrice) {
            newQuery.priceMax = null
          }
        }
        this.$router.push(
          {
            path: `/requests/categories/${newQuery.categoryId}`,
            query: newQuery
          },
          () => {}
        )
      } else {
        this.$router.push(
          {
            path: '/requests',
            query: newQuery
          },
          () => {}
        )
      }
    },
    changeKeyword(value) {
      const attribute = {
        keyword: value
      }
      if (this.currentCategoryId !== null) {
        attribute.categoryId = this.currentCategoryId
      }
      this.changeSearchForm(attribute)
    },
    changeRecruiting(value) {
      this.$cookies.set(REQUESTS_RECRUITING, value)
      const attribute = {
        recruiting: value
      }
      if (this.currentCategoryId !== null) {
        attribute.categoryId = this.currentCategoryId
      }
      this.changeSearchForm(attribute)
    },
    genBread() {
      this.bread.breadcrumb = [INIT_BREAD]
      const currentId = this.currentCategoryId
      const condition = this.search.condition
      if (currentId && condition.masterCategoriesList.length) {
        const parentCategory = {
          id: condition.masterCategoriesList[0].id,
          url: `/requests/categories/${condition.masterCategoriesList[0].id}`,
          isCurrent: condition.masterCategoriesList[0].isCurrent,
          name: condition.masterCategoriesList[0].name
        }
        this.bread.breadcrumb.push(parentCategory)

        if (!parentCategory.isCurrent && this.isChildCategory(condition, currentId)) {
          const sub = condition.masterCategoriesList[0].subMasterCategoriesList
          const childCategory = {
            id: this.setChildCategory(sub, currentId).id,
            url: `/requests/categories/${this.setChildCategory(sub, currentId).id}`,
            isCurrent: this.setChildCategory(sub, currentId).isCurrent,
            name: this.setChildCategory(sub, currentId).name
          }
          this.bread.breadcrumb.push(childCategory)
        }
      } else {
        const notSetCategory = {
          id: 0,
          url: '/requests',
          isCurrent: true,
          name: 'すべて'
        }
        this.bread.breadcrumb.push(notSetCategory)
      }
    },
    isChildCategory(condition, currentId) {
      return condition.masterCategoriesList[0].subMasterCategoriesList.some(
        category => category.id == currentId
      )
    },
    setChildCategory(sub, currentId) {
      return sub.find(category => category.id == currentId)
    },
    updateData() {
      this.recruiting = this.$util.castToBooleanQueryString(this.$route.query.recruiting) === true
      this._setKeyword()
    },
    _setKeyword() {
      this.keyword = this.$route.query.keyword ? this.$route.query.keyword : null
    },
    _setCategoryTitle(categoryId = null) {
      if (!categoryId) {
        return null
      }
      const categoriesList = this.$store.state.master.masterCategories
      let categoryName = ''

      categoriesList.forEach(category => {
        if (category.id == categoryId) {
          categoryName = category.name
        }

        category.subCategories.forEach(subCategory => {
          if (subCategory.id == categoryId) {
            categoryName = subCategory.name
          }
        })
      })

      return categoryName
    }
  },
  mounted() {
    this.recruiting = this.$util.castToBooleanQueryString(this.$route.query.recruiting)

    this.footerBottomBeforeMountedSp = this.$store.state.ui.footerBottomSp
    this.updateFooterBottomSp(FOOTER_BOTTOM_SP)
  },
  beforeDestroy() {
    this.updateFooterBottomSp(this.footerBottomBeforeMountedSp)
  }
}
</script>
<style lang="scss" scoped>
.c-searchPage {
  position: relative;
  margin: 0 auto;
  padding: 12px 20px 0;
  max-width: 1280px;

  &_breadcrumb {
    margin-left: 0;
  }

  &_mainContent {
    position: relative;

    &-loading {
      height: calc(100vh - 320px);
    }
  }

  &_loader {
    min-height: 100%;
  }

  &_itemList {
  }

  &_pagination {
    overflow: hidden;
    padding-top: 24px;
    background-color: $color-white;
  }

  &_paginationSummary {
    margin-top: 12px;
    color: $color-gray-600;
    text-align: center;
    font-size: 12px;
  }
}

.c-btnAddRequest {
  position: absolute;
  top: 56px;
  right: 20px;

  a {
    @include link-effect;

    color: $color-link-base;
  }
}

@media (max-width: breakpoint(Requests, S)) {
  .c-btnAddRequest {
    top: 8px;
    right: 12px;
  }
}

.c-searchNoHits {
  padding: 120px 0 0;
  text-align: center;
  font-size: 18px;

  .coconala-icon {
    position: relative;
    top: 2px;
    margin-right: 4px;
    color: $color-gray-400;
    font-size: 24px;
  }
}

@media (max-width: breakpoint(Requests, S)) {
  .c-searchPage {
    &_pagination {
      padding-bottom: 40px;
      background-color: $color-gray-50;

      &-app {
        padding-bottom: 80px;
      }
    }
  }

  .c-searchNoHits {
    padding: 40px 0 120px;
    background: $color-white;
    text-align: center;
    font-size: 14px;

    .coconala-icon {
      position: relative;
      top: 2px;
      margin-right: 4px;
      color: $color-gray-400;
      font-size: 18px;
    }
  }
}

.c-columns {
  display: flex;
  margin-top: 12px;

  justify-content: space-between;
}

@media (min-width: breakpoint(Requests, S)) {
  .c-columns {
    margin-top: 24px;

    &_side {
      min-width: 256px;

      flex-basis: 256px;
    }

    &_main {
      margin-left: 32px;
      min-width: 0;

      flex-grow: 1;
    }
  }
}

.c-searchTitle {
  margin-bottom: 12px;
  padding-right: 160px;
  font-size: 24px;
  line-height: 1.3;

  &Caption {
    font-weight: bold;
  }
}

@media (max-width: breakpoint(Requests, S)) {
  .c-searchTitle {
    margin-bottom: 0;
    padding: 0 12px;
    min-height: 32px;
    background: $color-white;
    font-size: 16px;
  }

  .c-searchPage {
    &_itemList {
      background-color: $color-gray-50;
    }
  }
}

/deep/ .c-searchKeyword {
  margin-bottom: 20px;
  padding: 28px 24px 0;
  background: $color-gray-50;

  .button,
  .input {
    height: 48px;
  }

  .c-requestSearchKeyword {
    margin: 0 auto;
    max-width: 680px;
  }

  &Description {
    display: flex;
    margin: 0 auto;
    padding: 8px 0 8px;
    max-width: 680px;

    justify-content: space-between;

    .flexItem {
      font-size: 12px;

      &Example {
      }

      &Help {
        text-align: right;
      }
    }
  }
}

@media (max-width: breakpoint(Requests, S)) {
  /deep/ .c-searchKeyword {
    margin-bottom: 0;
    padding: 12px;
    background: $color-white;

    .button,
    .input {
      height: 40px;
    }
  }
}

.c-searchHeader {
  display: flex;
  height: 40px;

  justify-content: space-between;

  &_result {
    flex: 1;

    align-self: center;
  }

  &_sort {
    flex-basis: 200px;

    /deep/ .select {
      &.is-empty select {
        color: $color-gray-800;
      }
    }
  }
}

@media (max-width: breakpoint(Requests, S)) {
  /deep/ .c-searchHeader {
    padding: 8px 0 12px;
    height: auto;

    flex-wrap: wrap;
    justify-content: center;

    &_result {
      margin-bottom: 4px;
      color: $color-gray-600;
      text-align: center;
      font-size: 11px;

      flex-basis: 100%;
    }
    &_resultHits {
      color: $color-gray-800;
      font-size: 14px;
    }
  }
}

.c-searchResults {
}

@media (max-width: breakpoint(Requests, S)) {
  /deep/ .c-searchResults {
    background-color: $color-white;
  }
}

.c-searchHeaderHits {
  display: inline-block;
  margin-right: 12px;
}

.c-searchHeaderRecruiting {
  position: relative;
  top: 1px;
  display: inline-block;
}

@media (max-width: breakpoint(Requests, S)) {
  .c-searchHeaderHits {
    display: block;
    margin-right: 0;
  }

  .c-searchHeaderRecruiting {
    position: relative;
    top: 0;
    padding: 4px 0;
  }

  .c-searchPage {
    &_breadcrumb {
      padding: 0 112px 0 12px;
    }
  }
}

@media (max-width: breakpoint(Requests, S)) {
  .c-searchPage {
    padding: 12px 0 0;
    max-width: 100%;

    &_header {
      padding: 0 16px 16px;
      background-color: $color-white;
    }
  }

  .c-columns {
    margin-top: 12px;
    background-color: $color-gray-50;

    &-loading {
      background-color: $color-white;
    }

    &_main {
      width: 100%;
    }
  }
}
</style>
